import { newId, nowIso } from "@/lib/uid";
import type { CatalogKitConfig } from "@/types";
import type { AutoMatchMethod, CatalogProfile } from "@/types/schemas";
import { CatalogProfileSchema } from "@/types/schemas";

import { logAction } from "./audit";
import { db } from "./schema";

/**
 * Profile CRUD. A profile is remembered catalog context ("Main Tenant CW",
 * "Acquisition Alpha") that snapshots attach to. Every mutating operation
 * writes an audit-log entry.
 */

/** Fields accepted when creating a profile (the rest are defaulted/generated). */
export interface ProfileInput {
  label: string;
  source_system?: string;
  auto_match_method?: AutoMatchMethod;
  retention_days?: number | null;
  notes?: string;
  default_config?: CatalogKitConfig;
}

/** Options for {@link listProfiles}. */
export interface ListProfilesOptions {
  /** Omit to return all; `false` for active only; `true` for archived only. */
  archived?: boolean;
  /** Sort key (all descending except `label`, which is A→Z). Default: `last_used_at`. */
  sortBy?: "last_used_at" | "created_at" | "label";
}

/** Create a profile, audit-logged. */
export async function createProfile(input: ProfileInput): Promise<CatalogProfile> {
  const now = nowIso();
  const profile = CatalogProfileSchema.parse({
    id: newId(),
    created_at: now,
    last_used_at: now,
    ...input,
  });
  await db.profiles.add(profile);
  await logAction({
    profile_id: profile.id,
    category: "profile_change",
    action: `Created profile "${profile.label}"`,
    actor: "user",
    details: { source_system: profile.source_system },
    after_state: profile,
  });
  return profile;
}

/** Fetch a profile by id, or `null` if it does not exist. */
export async function getProfile(id: string): Promise<CatalogProfile | null> {
  return (await db.profiles.get(id)) ?? null;
}

/** List profiles with optional archived filter and sort. */
export async function listProfiles(
  options: ListProfilesOptions = {},
): Promise<CatalogProfile[]> {
  const { archived, sortBy = "last_used_at" } = options;
  let profiles = await db.profiles.toArray();

  if (archived !== undefined) {
    profiles = profiles.filter((p) => p.archived === archived);
  }

  profiles.sort((a, b) => {
    if (sortBy === "label") return a.label.localeCompare(b.label);
    return b[sortBy].localeCompare(a[sortBy]); // ISO timestamps sort lexically; newest first
  });
  return profiles;
}

/** Apply a partial update, audit-logged. Throws if the profile is missing. */
export async function updateProfile(
  id: string,
  patch: Partial<Omit<CatalogProfile, "id">>,
): Promise<CatalogProfile> {
  const existing = await db.profiles.get(id);
  if (existing === undefined) throw new Error(`Profile not found: ${id}`);

  const updated = CatalogProfileSchema.parse({ ...existing, ...patch, id });
  await db.profiles.put(updated);
  await logAction({
    profile_id: id,
    category: "profile_change",
    action: `Updated profile "${updated.label}"`,
    actor: "user",
    details: { fields: Object.keys(patch) },
    before_state: existing,
    after_state: updated,
  });
  return updated;
}

/** Mark a profile archived (soft delete). */
export async function archiveProfile(id: string): Promise<void> {
  await updateProfile(id, { archived: true });
}

/**
 * Delete a profile.
 *
 * Args:
 *     id: Profile id.
 *     options.cascade: When `true`, also delete the profile's snapshots. When
 *         `false`, the delete is refused if any snapshots would be orphaned.
 */
export async function deleteProfile(
  id: string,
  options: { cascade: boolean },
): Promise<void> {
  const existing = await db.profiles.get(id);
  if (existing === undefined) throw new Error(`Profile not found: ${id}`);

  const snapshotIds = await db.snapshots.where("profile_id").equals(id).primaryKeys();

  if (!options.cascade && snapshotIds.length > 0) {
    throw new Error(
      `Profile "${existing.label}" has ${snapshotIds.length} snapshot(s); ` +
        `pass { cascade: true } to delete them too.`,
    );
  }

  await db.transaction("rw", db.profiles, db.snapshots, async () => {
    if (options.cascade && snapshotIds.length > 0) {
      await db.snapshots.bulkDelete(snapshotIds);
    }
    await db.profiles.delete(id);
  });

  await logAction({
    profile_id: id,
    category: "delete",
    action: `Deleted profile "${existing.label}"`,
    actor: "user",
    details: { cascade: options.cascade, snapshots_deleted: options.cascade ? snapshotIds.length : 0 },
    before_state: existing,
  });
}

/** Rename a profile, audit-logged. */
export async function renameProfile(id: string, newLabel: string): Promise<CatalogProfile> {
  return updateProfile(id, { label: newLabel });
}
